import { CSSProperties } from 'react';
import { Key, Sparkles } from 'lucide-react';
import { Email } from '@/types/email';
import { cn } from '@/lib/utils';
import { formatDistanceToNow } from 'date-fns';

interface EmailListItemProps {
  email: Email;
  isSelected: boolean;
  onSelect: () => void;
  style?: CSSProperties;
}

export const EmailListItem = ({
  email,
  isSelected,
  onSelect,
  style,
}: EmailListItemProps) => {
  const timeAgo = formatDistanceToNow(email.receivedAt, { addSuffix: true });

  return (
    <div
      onClick={onSelect}
      style={style}
      className={cn(
        'group flex items-start gap-4 p-4 rounded-2xl cursor-pointer transition-all duration-300 animate-slide-up mx-2',
        isSelected
          ? 'bg-inbox-selected border border-primary/25 shadow-glow'
          : 'hover:bg-inbox-hover border border-transparent hover:border-border/50',
        !email.isRead && !isSelected && 'bg-inbox-selected/40'
      )}
    >
      {/* Avatar / Icon */}
      <div className="relative">
        <div
          className={cn(
            'w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 transition-all duration-300',
            email.hasOTP
              ? 'gradient-bg shadow-glow'
              : 'bg-secondary group-hover:bg-secondary/80'
          )}
        >
          {email.hasOTP ? (
            <Key className="w-5 h-5 text-primary-foreground" />
          ) : (
            <span className="text-base font-bold text-secondary-foreground">
              {email.fromName.charAt(0).toUpperCase()}
            </span>
          )}
        </div>
        {email.hasOTP && (
          <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-accent flex items-center justify-center shadow-glow-accent animate-bounce-subtle">
            <Sparkles className="w-3 h-3 text-accent-foreground" />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 mb-1.5">
          <span
            className={cn(
              'text-sm truncate',
              !email.isRead ? 'font-bold text-foreground' : 'font-medium text-foreground/85'
            )}
          >
            {email.fromName}
          </span>
          <span className="text-xs text-muted-foreground shrink-0 font-medium">
            {timeAgo}
          </span>
        </div>

        <div className="flex items-center gap-2 mb-1.5">
          <span
            className={cn(
              'text-sm truncate flex-1',
              !email.isRead ? 'font-semibold text-foreground' : 'text-foreground/75'
            )}
          >
            {email.subject}
          </span>
          {email.hasOTP && (
            <span className="px-2.5 py-1 text-[10px] font-bold rounded-full gradient-bg text-primary-foreground shrink-0 uppercase tracking-wider shadow-glow">
              OTP
            </span>
          )}
        </div>

        <p className="text-sm text-muted-foreground truncate leading-relaxed">
          {email.preview}
        </p>
      </div>

      {/* Unread indicator */}
      {!email.isRead && (
        <div className="w-3 h-3 rounded-full bg-inbox-unread shrink-0 mt-1 shadow-glow animate-glow-pulse" />
      )}
    </div>
  );
};